import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useBackgroundVideoContainerAnalytics } from "./fire-analytics-event";
export interface UniformBackgroundVideoContainerProps {
    videoHeading: string;
    videoDescription?: string;
    videoSourceDesktop: string;
    videoSourceTablet: string;
    videoSourceMobile: string;
    videoCta?: string;
    videoCtaLink?: string;
    videoLoop?: boolean;
    videoWidthMobile?: number;
    videoHeightMobile?: number;
    videoWidthTablet?: number;
    videoHeightTablet?: number;
    videoWidthDesktop?: number;
    videoHeightDesktop?: number;
}

const containerStyles = css({
    backgroundColor: wacToken("color.neutral.dark0"),
    position: "relative",
    overflow: "hidden",
    height: "var(--video-height-desktop)",
    [media.below.lg]: {
        height: "var(--video-height-tablet)"
    },
    [media.below.md]: {
        height: "var(--video-height-mobile)"
    }
});

const contentStyles = css({
    opacity: 0,
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    margin: "auto",
    padding: "32px",
    maxWidth: "100%",
    height: "100%",
    width: "768px",
    textShadow: `0 0 10px ${wacToken(
        "color.primary.black"
    )}, 0 0 50px ${wacToken("color.primary.black")}`,
    transition: "opacity 0.6s ease-in-out",
    [media.below.md]: {
        width: "468px"
    },
    [`&.animate-reveal`]: {
        opacity: 1,
        transitionDelay: "0.8s"
    }
});

const svgOverlayStyles = css({
    position: "absolute",
    zIndex: 3,
    pointerEvents: "none",
    inset: 0,
    width: "100%",
    height: "100%",
    [`@media (prefers-reduced-motion: reduce)`]: {
        display: "none"
    }
});

const backgroundVideoStyles = css({
    position: "absolute",
    margin: "auto",
    insetBlockStart: 0,
    insetBlockEnd: 0,
    zIndex: 1,
    insetInlineStart: "calc(var(--video-width-desktop) * -1)",
    insetInlineEnd: "calc(var(--video-width-desktop) * -1)",
    height: "var(--video-height-desktop)",
    [media.below.lg]: {
        insetInlineStart: "calc(var(--video-width-tablet) * -1)",
        insetInlineEnd: "calc(var(--video-width-tablet) * -1)",
        height: "var(--video-height-tablet)"
    },
    [media.below.md]: {
        insetInlineStart: "calc(var(--video-width-mobile) * -1)",
        insetInlineEnd: "calc(var(--video-width-mobile) * -1)",
        height: "var(--video-height-mobile)"
    }
});

const headingStyles = css({
    margin: `0 0 ${token("space.200")}`,
    [`&.video-heading`]: {
        color: wacToken("color.neutral.light0")
    }
});

const descriptionStyles = css({
    [`&.video-description`]: {
        color: wacToken("color.neutral.light0")
    }
});

const controlButtonStyles = css({
    cursor: "pointer",
    opacity: 0.4,
    background: "transparent",
    color: wacToken("color.neutral.light0"),
    border: `2px solid`,
    borderRadius: wacToken("corner.radius.pill"),
    height: "30px",
    width: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    zIndex: 4,
    insetInlineEnd: token("space.200"),
    insetBlockEnd: token("space.200"),
    [`&:hover`]: {
        opacity: 1
    },
    [`&:focus-visible`]: {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px",
        opacity: 1
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`svg`]: {
        width: "14px"
    }
});

const UniformBackgroundVideoContainer = (
    props: UniformBackgroundVideoContainerProps
    // eslint-disable-next-line complexity
) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setPlaying] = useState(false);
    const { firePlayPauseClicked } = useBackgroundVideoContainerAnalytics();
    const [hasEnded, setHasEnded] = useState(false);
    const [currentVideoSource, setCurrentVideoSource] = useState<string>("");
    const [windowWidth, setWindowWidth] = useState<number>(
        typeof globalThis.window !== "undefined"
            ? globalThis.window.innerWidth
            : 1200
    );
    const [holeWidth, setHoleWidth] = useState<number>(1); // Separate width for rectangle
    const [holeHeight, setHoleHeight] = useState<number>(1); // Separate height for rectangle
    const [holeRadius, setHoleRadius] = useState<number>(300);
    const [containerDimensions, setContainerDimensions] = useState({
        width: 0,
        height: 0
    });
    const intl = useIntl();
    const prefersReducedMotion =
        typeof globalThis.window !== "undefined"
            ? globalThis.window.matchMedia("(prefers-reduced-motion: reduce)")
                  .matches
            : false;

    // can turn the feature flag on to disable the overlay if it's glitchy
    const isAnimationOverlayEnabled = checkFeatureFlagGate(
        featureFlags.gwp7823AnimationOverlayOption
    );

    const computedWidth = containerDimensions.width || 1200;
    const computedHeight = containerDimensions.height || 700;

    const getVideoSource = useCallback(
        (width: number): string => {
            if (width >= 1024) return props.videoSourceDesktop;
            if (width >= 768) return props.videoSourceTablet;
            return props.videoSourceMobile;
        },
        [
            props.videoSourceDesktop,
            props.videoSourceTablet,
            props.videoSourceMobile
        ]
    );

    useEffect(() => {
        const handleResize = () => {
            if (typeof globalThis.window !== "undefined") {
                setWindowWidth(globalThis.window.innerWidth);
            }
        };

        const updateVideoSource = (newSource: string) => {
            if (newSource === currentVideoSource) return;

            setCurrentVideoSource(newSource);
            if (!videoRef.current) return;

            if (isPlaying) {
                const currentTime = videoRef.current.currentTime;
                videoRef.current.src = newSource;
                videoRef.current.load();
                videoRef.current.currentTime = currentTime;
                videoRef.current.play().catch(() => {
                    // Ignore error likely due to autoplay restrictions or browser power saving
                });
            } else {
                videoRef.current.src = newSource;
                videoRef.current.load();
            }
        };

        const initialSource = getVideoSource(windowWidth);
        updateVideoSource(initialSource);

        if (typeof globalThis.window !== "undefined") {
            globalThis.window.addEventListener("resize", handleResize);
            return () =>
                globalThis.window.removeEventListener("resize", handleResize);
        }

        return undefined;
    }, [windowWidth, currentVideoSource, isPlaying, getVideoSource]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                firePlayPauseClicked("pause");
            } else {
                videoRef.current.play().catch(() => {
                    // Ignore error likely due to autoplay restrictions or browser power saving
                });
                firePlayPauseClicked("play");
            }
        }
    };

    const handleVideoPlay = () => {
        setPlaying(true);
        setHasEnded(false);
    };
    const handleVideoPause = () => setPlaying(false);
    const handleVideoEnded = () => {
        setPlaying(false);
        setHasEnded(true);
    };

    const startHoleAnimation = useCallback(
        (containerElement: HTMLDivElement) => {
            const containerWidth = containerElement.offsetWidth;
            const containerHeight = containerElement.offsetHeight;
            setContainerDimensions({
                width: containerWidth,
                height: containerHeight
            });

            setTimeout(() => {
                const startTime = Date.now();
                const duration = 800;
                const startSize = 1;

                const animationInterval = setInterval(() => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                    const currentHoleWidth =
                        startSize +
                        (containerWidth - startSize) * easedProgress;
                    const currentHoleHeight =
                        startSize +
                        (containerHeight - startSize) * easedProgress;
                    const currentRadius =
                        300 * (1 - Math.min(progress * 1.5, 1));
                    setHoleWidth(currentHoleWidth);
                    setHoleHeight(currentHoleHeight);
                    setHoleRadius(currentRadius);

                    if (progress >= 1) {
                        clearInterval(animationInterval);
                    }
                }, 16);
            }, 800);
        },
        []
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setIsVisible(true);
                    if (videoRef.current && !prefersReducedMotion) {
                        videoRef.current.play().catch(() => {
                            // Ignore error likely due to autoplay restrictions or browser power saving
                        });
                    }

                    if (
                        !prefersReducedMotion &&
                        containerRef.current &&
                        isAnimationOverlayEnabled
                    ) {
                        startHoleAnimation(containerRef.current);
                    } else if (
                        containerRef.current &&
                        isAnimationOverlayEnabled
                    ) {
                        const containerWidth = containerRef.current.offsetWidth;
                        const containerHeight =
                            containerRef.current.offsetHeight;
                        setContainerDimensions({
                            width: containerWidth,
                            height: containerHeight
                        });
                        setHoleWidth(containerWidth);
                        setHoleHeight(containerHeight);
                        setHoleRadius(0);
                    }
                }
            },
            { threshold: 0.3 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, [prefersReducedMotion, startHoleAnimation, isAnimationOverlayEnabled]);

    return (
        <div
            ref={containerRef}
            css={containerStyles}
            style={
                {
                    "--video-width-mobile": props.videoWidthMobile
                        ? `${props.videoWidthMobile}px`
                        : "500px",
                    "--video-height-mobile": props.videoHeightMobile
                        ? `${props.videoHeightMobile}px`
                        : "700px",
                    "--video-width-tablet": props.videoWidthTablet
                        ? `${props.videoWidthTablet}px`
                        : "800px",
                    "--video-height-tablet": props.videoHeightTablet
                        ? `${props.videoHeightTablet}px`
                        : "1000px",
                    "--video-width-desktop": props.videoWidthDesktop
                        ? `${props.videoWidthDesktop}px`
                        : "1200px",
                    "--video-height-desktop": props.videoHeightDesktop
                        ? `${props.videoHeightDesktop}px`
                        : "700px"
                } as React.CSSProperties
            }
        >
            <video
                ref={videoRef}
                css={backgroundVideoStyles}
                src={currentVideoSource}
                muted
                loop={props.videoLoop || false}
                playsInline
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onEnded={handleVideoEnded}
            />

            <div
                css={contentStyles}
                className={isVisible ? "animate-reveal" : ""}
            >
                <h2 className="video-heading" css={headingStyles}>
                    {props.videoHeading}
                </h2>
                {props.videoDescription && (
                    <p className="video-description" css={descriptionStyles}>
                        {props.videoDescription}
                    </p>
                )}
                {props.videoCta && props.videoCtaLink && (
                    <Link
                        aria-label={props.videoCta}
                        href={props.videoCtaLink}
                        text={props.videoCta}
                        type={"primary"}
                        bgColor="bgBlackBlue"
                        dataEventProps={{
                            "data-product-key": "uniform",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "videoContainer"
                        }}
                    />
                )}
            </div>

            {!hasEnded && (
                <button
                    type="button"
                    css={controlButtonStyles}
                    onClick={togglePlay}
                    aria-label={intl.formatMessage(
                        isPlaying
                            ? {
                                  id: "uniform-video.pause",
                                  defaultMessage: "Pause"
                              }
                            : {
                                  id: "uniform-video.play",
                                  defaultMessage: "Play"
                              }
                    )}
                    aria-pressed={isPlaying}
                >
                    {isPlaying ? (
                        <VideoPauseOverlayIcon
                            label={intl.formatMessage({
                                id: "uniform-video.pause",
                                defaultMessage: "Pause"
                            })}
                        />
                    ) : (
                        <VideoPlayOverlayIcon
                            label={intl.formatMessage({
                                id: "uniform-video.play",
                                defaultMessage: "Play"
                            })}
                        />
                    )}
                </button>
            )}

            {isAnimationOverlayEnabled && (
                <svg
                    css={svgOverlayStyles}
                    viewBox={`0 0 ${computedWidth} ${computedHeight}`}
                    preserveAspectRatio="none"
                >
                    <defs>
                        <mask id="hole-mask">
                            <rect
                                width={computedWidth}
                                height={computedHeight}
                                fill="white"
                            />
                            <rect
                                x={computedWidth / 2 - holeWidth / 2}
                                y={computedHeight / 2 - holeHeight / 2}
                                width={holeWidth}
                                height={holeHeight}
                                rx={holeRadius}
                                ry={holeRadius}
                                fill="black"
                            />
                        </mask>
                    </defs>
                    <rect
                        width={computedWidth}
                        height={computedHeight}
                        fill={wacToken("color.neutral.light0")}
                        mask="url(#hole-mask)"
                    />
                </svg>
            )}
        </div>
    );
};

export default UniformBackgroundVideoContainer;
